import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import {
  VerifiedUser,
  Email,
  Phone,
  Cake,
  LocalHospital,
  Home,
  Bloodtype,
  Badge,
} from '@mui/icons-material';
import axios from 'axios';
import koalogo from '../assets/koalogo.png';
import MemberPortalFooter from './MemberPortalFooter';

const API_BASE_URL = 'https://koaapi.zinvos.com';

const PublicProfile = () => {
  const { koalmNumber } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchMember = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/public/member/${koalmNumber}/`
        );
        setMember(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Member not found');
      } finally {
        setLoading(false);
      }
    };

    if (koalmNumber) {
      fetchMember();
    }
  }, [koalmNumber]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress sx={{ color: 'white' }} size={60} />
        </Box>
        <MemberPortalFooter />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 3,
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={10}
              sx={{
                padding: 4,
                borderRadius: 3,
                background: 'white',
                textAlign: 'center',
              }}
            >
              <Alert severity="error" sx={{ fontSize: '1.1rem' }}>
                {error}
              </Alert>
            </Paper>
          </Container>
        </Box>
        <MemberPortalFooter />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        py: { xs: 3, md: 6 },
        px: 2,
      }}
    >
      <Container maxWidth="md">
        {/* Header Card */}
        <Paper
          elevation={0}
          sx={{
            background: 'white',
            borderRadius: 3,
            overflow: 'hidden',
            mb: 3,
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
              py: 3,
              px: 4,
              textAlign: 'center',
            }}
          >
            <Box
              component="img"
              src={koalogo}
              alt="KOA Logo"
              sx={{
                maxWidth: 120,
                height: 'auto',
                mb: 2,
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              fontWeight="700"
              sx={{ color: 'white', mb: 0.5, letterSpacing: 1 }}
            >
              KERALA ORTHOPAEDIC ASSOCIATION
            </Typography>
            <Chip
              icon={<VerifiedUser />}
              label="Verified Member Profile"
              sx={{
                mt: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: 'white',
                },
              }}
            />
          </Box>
        </Paper>

        {/* Main Profile Card */}
        <Paper
          elevation={8}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: 'white',
          }}
        >
          {/* Photo and Name Section */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              pt: 5,
              pb: 3,
              px: 3,
              textAlign: 'center',
            }}
          >
            <Avatar
              src={member?.photo}
              sx={{
                width: { xs: 160, md: 200 },
                height: { xs: 160, md: 200 },
                margin: '0 auto',
                border: '6px solid white',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                mb: 2,
                '& img': {
                  objectFit: 'cover',
                },
              }}
            />
            <Typography
              variant="h4"
              fontWeight="700"
              sx={{
                color: '#1e3c72',
                mb: 1,
                fontSize: { xs: '1.75rem', md: '2.125rem' },
              }}
            >
              {member?.name || 'N/A'}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: '1rem',
              }}
            >
              Member, Kerala Orthopaedic Association
            </Typography>
          </Box>

          <Divider />

          {/* Information Grid */}
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h6"
              fontWeight="600"
              sx={{ mb: 3, color: '#1e3c72' }}
            >
              Member Information
            </Typography>
            
            <Grid container spacing={2.5}>
              {member?.date_of_birth && (
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Cake sx={{ color: '#1e3c72', mr: 1, fontSize: 20 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          Date of Birth
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="600" sx={{ color: '#2c3e50' }}>
                        {new Date(member.date_of_birth).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {member?.blood_group && (
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Bloodtype sx={{ color: '#d32f2f', mr: 1, fontSize: 20 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          Blood Group
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="600" sx={{ color: '#2c3e50' }}>
                        {member.blood_group}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {member?.email && (
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Email sx={{ color: '#1e3c72', mr: 1, fontSize: 20 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          Email Address
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        fontWeight="600"
                        sx={{
                          color: '#2c3e50',
                          wordBreak: 'break-word',
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        }}
                      >
                        {member.email}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {member?.mobile_number && (
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Phone sx={{ color: '#1e3c72', mr: 1, fontSize: 20 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          Mobile Number
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="600" sx={{ color: '#2c3e50' }}>
                        {member.mobile_number}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {member?.IOA_LM_number && (
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Badge sx={{ color: '#1e3c72', mr: 1, fontSize: 20 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          IOA LM Number
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="600" sx={{ color: '#2c3e50' }}>
                        {member.IOA_LM_number}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {member?.district_club_name && (
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Home sx={{ color: '#1e3c72', mr: 1, fontSize: 20 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          Home Club
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="600" sx={{ color: '#2c3e50' }}>
                        {member.district_club_name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {member?.working_hospital_name && (
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocalHospital sx={{ color: '#1e3c72', mr: 1, fontSize: 20 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          Working Hospital
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="600" sx={{ color: '#2c3e50' }}>
                        {member.working_hospital_name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              py: 3,
              px: 3,
              textAlign: 'center',
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <VerifiedUser sx={{ color: '#1e3c72', fontSize: 32, mb: 1 }} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              This is an official verified member profile of the
              <br />
              <strong style={{ color: '#1e3c72' }}>Kerala Orthopaedic Association</strong>
            </Typography>
          </Box>
        </Paper>

        {/* Copyright */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 3,
            color: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          © {new Date().getFullYear()} Kerala Orthopaedic Association. All rights reserved.
        </Typography>
      </Container>
      <MemberPortalFooter />
    </Box>
  );
};

export default PublicProfile;

